import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import { Loader } from "@googlemaps/js-api-loader"; // Import Google Maps Loader
import { generateImages } from '../utils/imageUtils';
import { ProgressiveImage } from './ProgressiveImage';
import { generateTagsWithGemini } from '../lib/geminiUtils'; // Import the Gemini utility
import './CreatePost.css';

interface ImagePreview {
  file: File;
  display: Blob;
  thumbnail: Blob;
  preview: string;
  name: string;
  description: string;
  tags?: string[];
}

// Interface for selected restaurant details
interface SelectedRestaurant {
  placeId: string;
  name: string;
  address: string;
  city?: string;
  postcode?: string;
  country?: string;
  primaryType?: string;
  latitude?: number;
  longitude?: number;
}

// Add state for suggested tags
interface SuggestedTagsState {
  [index: number]: {
    tags: string[] | null;
    loading: boolean;
    error: string | null;
  };
}

export default function CreatePost() {
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [restaurantSearch, setRestaurantSearch] = useState(''); // State for search input display
  const [selectedRestaurant, setSelectedRestaurant] = useState<SelectedRestaurant | null>(null); // State for selected place
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState<SuggestedTagsState>({}); // State for suggested tags
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const autocompleteContainerRef = useRef<HTMLDivElement>(null); // Ref for the container of the autocomplete element

  // --- Google Maps Autocomplete Setup ---
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const [isMapsApiLoaded, setIsMapsApiLoaded] = useState(false); // Track API load state

  // Load Google Maps API
  useEffect(() => {
    if (!googleMapsApiKey) {
      console.error("Google Maps API Key is missing.");
      setError("Restaurant search is unavailable. Missing API Key.");
      return;
    }

    const loader = new Loader({
      apiKey: googleMapsApiKey,
      version: "weekly",
      libraries: ["places"],
    });

    loader.load()
      .then(() => setIsMapsApiLoaded(true))
      .catch(e => {
         console.error("Error loading Google Maps API:", e);
         setError("Failed to load restaurant search functionality.");
      });
  }, [googleMapsApiKey]);

  // Initialize Autocomplete Element
  useEffect(() => {
    if (!isMapsApiLoaded || !autocompleteContainerRef.current) return;

    // Ensure the container is empty before appending
    autocompleteContainerRef.current.innerHTML = '';

    // Explicitly cast to avoid TypeScript errors before google types might be fully loaded
    const google = window.google; // Removed 'as any' since types should be installed

    // Check if PlaceAutocompleteElement is available
    if (!google?.maps?.places?.PlaceAutocompleteElement) {
        console.error("PlaceAutocompleteElement not available.");
        setError("Restaurant search feature is currently unavailable.");
        return;
    }

    const placeAutocompleteElement = new google.maps.places.PlaceAutocompleteElement({
        // Removed inputElement option
        requestedLanguage: 'en', // Optional: Specify language
        types: ['establishment'] // Bias towards establishments
    });

    // Append the web component itself to the container
    autocompleteContainerRef.current.appendChild(placeAutocompleteElement);

    // Style the web component directly (placeholder, classes)
    // Note: Styling capabilities might depend on the component's implementation
    placeAutocompleteElement.setAttribute('placeholder', 'Search for a restaurant...');
    // Add relevant classes if needed for Tailwind - target the container or the element
    // autocompleteContainerRef.current.className += ' some-styling-class'; // Example on container
    // placeAutocompleteElement.classList.add('w-full', 'border', 'border-gray-300', 'rounded-md'); // Example if component allows direct class setting
    // For now, rely on container styling or potentially global CSS targeting gmp-place-autocomplete input

    // Add event listener for place selection
    placeAutocompleteElement.addEventListener('gmp-select', async (event: Event) => {
      console.log('Place selection event received:', event);
      console.log('Event type:', event.type);
      console.log('Event target:', event.target);

      // Cast the event to access the placePrediction property
      const selectEvent = event as CustomEvent<{ placePrediction: google.maps.places.PlacePrediction | null }>;
      const placePrediction = selectEvent.detail?.placePrediction; // Access via detail as per some examples, but could be directly on event

      if (!placePrediction) {
         // Attempt direct access if detail is null/undefined (handle potential API variations)
         const directPlacePrediction = (event as any).placePrediction as google.maps.places.PlacePrediction | null;
         if (!directPlacePrediction) {
            console.error('No place prediction found in the selection event', { detail: selectEvent.detail, event: event });
            setSelectedRestaurant(null);
            setRestaurantSearch('');
            return;
         }
         // Use directPlacePrediction if found
         console.log('Found placePrediction directly on event object.');
         const place = directPlacePrediction.toPlace();

         try {
            // Ensure we have all required fields by fetching them
            // Note: We specify 'id' here though it might be implicitly available
            console.log('Fetching required place details (direct)...');
            const fields = await place.fetchFields({
              fields: ['id', 'displayName', 'formattedAddress', 'types', 'primaryType', 'location', 'addressComponents']
            });
            console.log('Place Types (direct): ', fields.place.types);
            console.log('Primary Type (direct): ', fields.place.primaryType);

            if (fields.place.id && fields.place.displayName && fields.place.formattedAddress) {
              const location = fields.place.location;
              
              // Extract components from addressComponents array
              let city = '';
              let postcode = '';
              let country = '';
              const addressComponents = fields.place.addressComponents || [];
              console.log('Address Components Received (direct):', addressComponents); // Log the whole array
              for (const component of addressComponents as any) { // Use 'as any' for component
                console.log('Processing Component (direct):', component); // Log each component
                // Access properties by name (Fg, Eg)
                const componentLongName = component.Fg; 
                const componentTypes = component.Eg || []; // types array is in Eg

                if (componentTypes.includes('locality') || componentTypes.includes('postal_town')) {
                  city = componentLongName;
                } else if (componentTypes.includes('postal_code')) {
                  postcode = componentLongName;
                } else if (componentTypes.includes('country')) {
                  country = componentLongName;
                }
              }

              const restaurantData = {
                placeId: fields.place.id,
                name: fields.place.displayName,
                address: fields.place.formattedAddress,
                city,
                postcode,
                country,
                primaryType: fields.place.primaryType || undefined,
                latitude: location ? location.lat() : undefined,
                longitude: location ? location.lng() : undefined
              };
              console.log('Restaurant location:', {
                latitude: restaurantData.latitude,
                longitude: restaurantData.longitude,
                city: restaurantData.city,
                postcode: restaurantData.postcode,
                country: restaurantData.country
              });
              setSelectedRestaurant(restaurantData);
              setRestaurantSearch(fields.place.displayName); // Update search display text
              console.log('Restaurant selected (direct):', restaurantData);
            } else {
              console.error('Incomplete place details after fetching (direct):', fields);
              setSelectedRestaurant(null);
              setRestaurantSearch(''); // Clear display text on error
            }
          } catch (error) {
            console.error('Error processing place details (direct):', error);
            setError('Failed to process restaurant details');
            setSelectedRestaurant(null);
            setRestaurantSearch(''); // Clear display text on error
          }
          return; // Exit after handling direct access case
       }


      // If placePrediction was found via event.detail
      console.log('Found placePrediction in event.detail.');
      const place = placePrediction.toPlace();

      try {
        // Ensure we have all required fields by fetching them
        // Note: We specify 'id' here though it might be implicitly available
        console.log('Fetching required place details (detail)...');
        const fields = await place.fetchFields({
          fields: ['id', 'displayName', 'formattedAddress', 'types', 'primaryType', 'location', 'addressComponents']
        });
        console.log('Place Types (detail): ', fields.place.types);
        console.log('Primary Type (detail): ', fields.place.primaryType);

        if (fields.place.id && fields.place.displayName && fields.place.formattedAddress) {
          const location = fields.place.location;
          
          // Extract components from addressComponents array
          let city = '';
          let postcode = '';
          let country = '';
          const addressComponents = fields.place.addressComponents || [];
          console.log('Address Components Received (detail):', addressComponents); // Log the whole array
          for (const component of addressComponents as any) { // Use 'as any' for component
            console.log('Processing Component (detail):', component); // Log each component
            // Access properties by name (Fg, Eg)
            const componentLongName = component.Fg; 
            const componentTypes = component.Eg || []; // types array is in Eg

            if (componentTypes.includes('locality') || componentTypes.includes('postal_town')) {
              city = componentLongName;
            } else if (componentTypes.includes('postal_code')) {
              postcode = componentLongName;
            } else if (componentTypes.includes('country')) {
              country = componentLongName;
            }
          }

          const restaurantData = {
            placeId: fields.place.id,
            name: fields.place.displayName,
            address: fields.place.formattedAddress,
            city,
            postcode,
            country,
            primaryType: fields.place.primaryType || undefined,
            latitude: location ? location.lat() : undefined,
            longitude: location ? location.lng() : undefined
          };
          console.log('Restaurant location:', {
            latitude: restaurantData.latitude,
            longitude: restaurantData.longitude,
            city: restaurantData.city,
            postcode: restaurantData.postcode,
            country: restaurantData.country
          });
          setSelectedRestaurant(restaurantData);
          setRestaurantSearch(fields.place.displayName); // Update search display text
          console.log('Restaurant selected (detail):', restaurantData);
        } else {
          console.error('Incomplete place details after fetching (detail):', fields);
          setSelectedRestaurant(null);
          setRestaurantSearch(''); // Clear display text on error
        }
      } catch (error) {
        console.error('Error processing place details (detail):', error);
        setError('Failed to process restaurant details');
        setSelectedRestaurant(null);
        setRestaurantSearch(''); // Clear display text on error
      }
    });

     // Removed manual input listener, component handles its state.

  }, [isMapsApiLoaded]); // Rerun when API is loaded


  // Note: handleRestaurantSearchChange is removed as it's handled internally by the component/gmp-select listener

  // --- End Google Maps Autocomplete Setup ---

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      const newImages = await Promise.all(
        Array.from(e.dataTransfer.files).map(async (file) => {
          const { display, thumbnail } = await generateImages(file);
          return {
            file,
            display,
            thumbnail,
            preview: URL.createObjectURL(display),
            name: file.name,
            description: '',
            tags: []
          };
        })
      );
      setImages(prev => [...prev, ...newImages]);
      // Initialize suggested tags state for new images
      initializeSuggestedTags(images.length, newImages.length);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newImages = await Promise.all(
        Array.from(e.target.files).map(async (file) => {
          const { display, thumbnail } = await generateImages(file);
          return {
            file,
            display,
            thumbnail,
            preview: URL.createObjectURL(display),
            name: file.name,
            description: '',
            tags: []
          };
        })
      );
      setImages(prev => [...prev, ...newImages]);
      // Initialize suggested tags state for new images
      initializeSuggestedTags(images.length, newImages.length);
    }
  };

  // Helper to initialize state for new images
  const initializeSuggestedTags = (startIndex: number, count: number) => {
    setSuggestedTags(prev => {
      const newState = { ...prev };
      for (let i = 0; i < count; i++) {
        newState[startIndex + i] = { tags: null, loading: false, error: null };
      }
      return newState;
    });
  };

  const handleImageMetadataChange = (index: number, field: 'name' | 'description', value: string) => {
    setImages(prev => 
      prev.map((img, i) => 
        i === index ? { ...img, [field]: value } : img
      )
    );
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    // Remove corresponding suggested tags state
    setSuggestedTags(prev => {
      const newState = { ...prev };
      delete newState[index];
      // Adjust indices for subsequent items if necessary (though simple deletion might suffice)
      return newState;
    });
  };

  // Function to handle Gemini tag generation
  const handleGenerateTags = async (index: number) => {
    const image = images[index];
    if (!image) return;

    // Update state to show loading
    setSuggestedTags(prev => ({
      ...prev,
      [index]: { ...prev[index], loading: true, error: null }
    }));

    try {
      const tags = await generateTagsWithGemini(image.display);
      setSuggestedTags(prev => ({
        ...prev,
        [index]: { tags: tags, loading: false, error: null }
      }));
    } catch (err) {
      console.error("Error generating tags:", err);
      setSuggestedTags(prev => ({
        ...prev,
        [index]: { tags: null, loading: false, error: err instanceof Error ? err.message : 'Failed to generate tags' }
      }));
    }
  };

  // Function to add suggested tags to the image description
  const handleAcceptTags = (index: number) => {
    const suggestion = suggestedTags[index];
    if (!suggestion || !suggestion.tags || suggestion.tags.length === 0) return;

    const tagsString = suggestion.tags.map(tag => `#${tag}`).join(' ');
    setImages(prev =>
      prev.map((img, i) =>
        i === index
          ? { ...img, description: `${img.description ? img.description + '\n' : ''}${tagsString}`.trim() }
          : img
      )
    );
    // Optionally clear the suggestions after accepting
    // setSuggestedTags(prev => ({ ...prev, [index]: { ...prev[index], tags: null } }));
  };

  // Function to dismiss suggested tags
  const handleDismissTags = (index: number) => {
    setSuggestedTags(prev => ({ ...prev, [index]: { ...prev[index], tags: null, error: null } }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    console.log('handleSubmit called. selectedRestaurant:', selectedRestaurant);

    try {
      setError('');
      setLoading(true);

      // Get the Firebase ID token
      const idToken = await currentUser.getIdToken();

      // Create FormData for the request
      const formData = new FormData();
      
      // Append files and create metadata array
      const photoMetadata: { 
        filename: string, 
        name: string, 
        description: string,
        displayFilename: string,
        thumbnailFilename: string,
        tags?: string[]
      }[] = [];
      images.forEach((image, index) => {
        // Append display and thumbnail images
        formData.append('displayImages', image.display, `display_${image.file.name}`);
        formData.append('thumbnailImages', image.thumbnail, `thumbnail_${image.file.name}`);
        
        photoMetadata.push({
          filename: image.file.name,
          displayFilename: `display_${image.file.name}`,
          thumbnailFilename: `thumbnail_${image.file.name}`,
          name: image.name,
          description: image.description,
          tags: suggestedTags[index]?.tags || undefined
        });
      });

      // Append metadata as a JSON string
      formData.append('photoMetadata', JSON.stringify(photoMetadata));

      formData.append('title', title);
      formData.append('content', content);
      formData.append('visitDate', visitDate);
      // Append selected restaurant details if available
      if (selectedRestaurant) {
        formData.append('placeId', selectedRestaurant.placeId);
        formData.append('restaurantName', selectedRestaurant.name);
        formData.append('restaurantAddress', selectedRestaurant.address);
        if (selectedRestaurant.city) {
          formData.append('restaurantCity', selectedRestaurant.city);
        }
        if (selectedRestaurant.postcode) {
          formData.append('restaurantPostcode', selectedRestaurant.postcode);
        }
        if (selectedRestaurant.country) {
          formData.append('restaurantCountry', selectedRestaurant.country);
        }
        if (selectedRestaurant.primaryType) {
          formData.append('restaurantPrimaryType', selectedRestaurant.primaryType);
        }
        if (selectedRestaurant.latitude) {
          formData.append('restaurantLatitude', selectedRestaurant.latitude.toString());
        }
        if (selectedRestaurant.longitude) {
          formData.append('restaurantLongitude', selectedRestaurant.longitude.toString());
        }
      }

      const response = await fetch(API_ENDPOINTS.POSTS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to create post');
      }

      navigate('/');
    } catch (err) {
      setError('Failed to create post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-post-page">
      <div className="create-post-card">
        <div className="create-post-header">
          <h2>Create New Post</h2>
          <p className="subtitle">Share your food experience with the community</p>
        </div>
        
        {error && (
          <div className="error-message">
            <svg className="error-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="create-post-form">
          <div className="form-section">
            <label className="form-label">
              <span className="label-text">Title</span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="form-input"
                placeholder="Give your post a catchy title"
              />
            </label>
          </div>

          <div className="form-section">
            <label className="form-label">
              <span className="label-text">Content</span>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
                rows={3}
                className="form-textarea"
                placeholder="Share your experience and thoughts about the food..."
              />
            </label>
          </div>

          <div className="form-section">
            <label className="form-label">
              <span className="label-text">Visit Date</span>
              <div className="date-picker-container">
                <input
                  type="date"
                  value={visitDate}
                  onChange={(e) => setVisitDate(e.target.value)}
                  required
                  className="form-input date-picker"
                  max={new Date().toISOString().split('T')[0]} // Prevent future dates
                  placeholder="Select visit date"
                />
              </div>
            </label>
          </div>

          <div className="form-section">
            <label className="form-label">
              <span className="label-text">Restaurant</span>
              <div ref={autocompleteContainerRef} className="restaurant-search-container">
                {!isMapsApiLoaded && !error && (
                  <div className="loading-indicator">
                    <div className="loading-spinner"></div>
                    <span>Loading restaurant search...</span>
                  </div>
                )}
              </div>
              {selectedRestaurant && (
                <div className="selected-restaurant">
                  <svg className="location-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  <div className="restaurant-info">
                    <span className="restaurant-name">{selectedRestaurant.name}</span>
                    <span className="restaurant-address">{selectedRestaurant.address}</span>
                  </div>
                </div>
              )}
            </label>
          </div>

          <div className="form-section">
            <label className="form-label">
              <span className="label-text">Photos</span>
              <div 
                className={`image-upload-container ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="upload-content">
                  <svg className="upload-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                  </svg>
                  <div className="upload-text">
                    <span>Drag and drop photos here</span>
                    <span className="upload-subtext">or</span>
                    <label className="upload-button">
                      Browse Files
                      <input
                        type="file"
                        className="sr-only"
                        multiple
                        accept="image/*"
                        onChange={handleImageChange}
                        ref={fileInputRef}
                      />
                    </label>
                  </div>
                  <p className="upload-hint">PNG, JPG, GIF up to 10MB</p>
                </div>
              </div>
            </label>
          </div>

          {images.length > 0 && (
            <div className="image-preview-section">
              <div className="image-grid">
                {images.map((image, index) => (
                  <div key={index} className="image-preview-card">
                    <div className="image-container">
                      <ProgressiveImage
                        src={image.preview}
                        thumbnailSrc={URL.createObjectURL(image.thumbnail)}
                        alt={`Preview ${index + 1}`}
                        className="preview-image"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="remove-image-button"
                        title="Remove image"
                      >
                        <svg viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <div className="image-details">
                      <input
                        type="text"
                        value={image.name}
                        onChange={(e) => handleImageMetadataChange(index, 'name', e.target.value)}
                        placeholder="Dish Name"
                        className="dish-name-input"
                      />
                      <textarea
                        id={`description-${index}`}
                        value={image.description}
                        onChange={(e) => handleImageMetadataChange(index, 'description', e.target.value)}
                        placeholder="Optional description for this photo..."
                        rows={2}
                        className="w-full mt-1 p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Generate Tags Button - show only if not loading/error and no tags yet */}
                    {(!suggestedTags[index] || (!suggestedTags[index].tags && !suggestedTags[index].loading && !suggestedTags[index].error)) && (
                      <button
                        type="button"
                        onClick={() => handleGenerateTags(index)}
                        disabled={suggestedTags[index]?.loading}
                        className="mt-2 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                      >
                        {suggestedTags[index]?.loading ? 'Generating...' : '✨ Suggest Tags (AI)'}
                      </button>
                    )}

                    {/* Suggested Tags Section */}
                    {suggestedTags[index] && (
                      <div className="mt-2 p-2 border border-gray-200 rounded bg-gray-50 suggested-tags-container">
                        {suggestedTags[index].loading && <p className="text-sm text-gray-500">Generating tags...</p>}
                        {suggestedTags[index].error && <p className="text-sm text-red-500">Error: {suggestedTags[index].error}</p>}
                        {suggestedTags[index].tags && suggestedTags[index].tags!.length > 0 && (
                          <>
                            <p className="text-sm font-medium mb-1">Suggested Tags:</p>
                            <div className="flex flex-wrap gap-1 mb-2">
                              {suggestedTags[index].tags!.map((tag, tagIndex) => (
                                <span key={tagIndex} className="suggested-tag">
                                  {tag}
                                </span>
                              ))}
                            </div>
                            <div className="flex gap-2 suggested-tags-actions">
                              <button
                                type="button"
                                onClick={() => handleAcceptTags(index)}
                                className="accept-tags-button"
                              >
                                Accept All
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDismissTags(index)}
                                className="dismiss-tags-button"
                              >
                                Dismiss
                              </button>
                            </div>
                          </>
                        )}
                        {suggestedTags[index].tags && suggestedTags[index].tags!.length === 0 && !suggestedTags[index].loading && !suggestedTags[index].error && (
                           <p className="text-sm text-gray-500">No tags suggested.</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              disabled={loading || images.length === 0}
              className={`submit-button ${loading ? 'loading' : ''}`}
            >
              {loading ? (
                <>
                  <div className="button-spinner"></div>
                  <span>Creating Post...</span>
                </>
              ) : (
                'Create Post'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 